/**
 * WIDERRUF · NEUBAU — der gemeinsame Rahmen der drei Schirme.
 * Entwurf `redesign/konzepte/kundenbetreuung/widerruf-neubau/` (8 Schirme).
 *
 * ═══ 🔑 DIE ADRESSE IST DIE ROLLE ══════════════════════════════════════════
 * Der Klickdummy hat einen Rollen-Umschalter mit vier Knöpfen — und jeder
 * Knopf schreibt eine andere **Adresse** in die Brotkrume: `/widerrufe`,
 * `/kb/widerrufe`, `/finance/widerrufe`. Genau diese drei Routen gibt es in
 * der App bereits (Kommentar in `router/routes.ts`: „DREI Routen auf DIESELBE
 * View — mit Absicht"). Der Umschalter ist also kein Klickdummy-Behelf,
 * sondern beschreibt, was schon da ist.
 *
 * 🔑 Deshalb wird „du" hier **aus der Route** gelesen, nicht aus einem
 * Zustand: *wer den Fall ansieht, ist keine Eigenschaft des Falls.* Die vierte
 * Rolle des Entwurfs (`kb_ma` — Kundenbetreuung ohne Bereichsleitung) ergibt
 * sich zusätzlich aus der Berechtigung, denn für die KB quittiert nur die BL.
 *
 * ═══ ⚠️ WAS AUS DEM ENTWURF NICHT ÜBERNOMMEN IST ═══════════════════════════
 * `w-nav` (eigene Navigationsleiste), `w-dummybadge`, `w-roles`. Die App hat
 * ihre Sidebar; eine zweite Navigation daneben wäre eine zweite Wahrheit
 * darüber, wo man steht.
 */

import "../../styles/terminstrecke-v2.css";
import "../../styles/widerruf-neubau.css";

// ═══ 🔴 DIE STYLESHEETS BLEIBEN HIER — ein Rueckschlag vom 20.09.2026 ══════
// Fuer den rollenabhaengigen Seitenleisten-Zaehler (J5) wollte ich sie in die
// vier Ansichten verschieben, damit die Leiste `wartetAuf` benutzen kann, ohne
// das Widerruf-CSS auf jede Seite zu ziehen.
//
// 📏 Gemessen: **23 von 23** Optik-Tests gruen vorher, **17 rot** danach. Andere
// Ansichten beziehen `widerruf-neubau.css` ueber dieses Modul mit — sie haetten
// ungestylt dagestanden (`duflag`, `st`, `w-dacts`), und zwar ohne Fehlermeldung.
//
// 🔑 ***Ein Modul, an dem andere haengen, ist kein Ort fuer eine Aufraeumung, die
// nur mir nuetzt.*** Der Preis der Ruecknahme ist benannt: das Widerruf-CSS liegt
// im Startbuendel. Das ist verkraftbar, seit alle Regeln an `.ve2-wrap` haengen —
// sichtbar wirkt es nur dort, wo es soll.
// 📌 Gefunden hat es der Optik-Waechter einer anderen Sitzung, nicht ich.
import { escapeHtml } from "../../lib/html";
import { appFormatDate } from "../../lib/dates";
import type { UserContext } from "../../api/types";
import {
  PARTEI_LABEL,
  type QuittungPartei, type WiderrufFall, type WiderrufZustand
} from "../../api/widerruf";

// ═══════════════════════════════════════════════════════════════════════════
// ADRESSE UND ROLLE
// ═══════════════════════════════════════════════════════════════════════════

export type WRolle = "vertrieb" | "kb" | "kb_ma" | "finance";

/** Die drei Einstiege. Reihenfolge egal — es wird auf Präfix geprüft. */
export const W_BASEN = ["/kb/widerrufe", "/finance/widerrufe", "/widerrufe"] as const;

/**
 * Welcher der drei Einstiege ist gemeint? Aufzurufen mit `location.pathname`.
 *
 * 🔴 **Nicht mit `location.hash`.** Dieser Router arbeitet über die History-API
 * (`history.pushState` in `router.ts`), der Hash ist immer leer. Die erste
 * Fassung las den Hash — und fiel damit auf jeder Adresse in den Rückfallwert
 * `/widerrufe`, also **still in die Vertriebs-Sicht**, auch für die
 * Kundenbetreuung. 🔑 *Ein Rückfallwert, der plausibel aussieht, verbirgt, dass
 * gar nichts gelesen wurde.*
 *
 * ⚠️ Selbstkorrektur nach der Gegenprobe: hier stand erst, die Reihenfolge in
 * `W_BASEN` sei die Absicherung gegen ein Verschlucken von `/kb/widerrufe`
 * durch `/widerrufe`. **Das stimmt nicht** — verglichen wird auf `===` oder
 * `startsWith(b + "/")`, und `/kb/widerrufe` beginnt nicht mit `/widerrufe`.
 * Die Reihenfolge ist gleichgültig, solange keiner der drei Pfade Präfix eines
 * anderen ist. Der Test pinnt deshalb das VERHALTEN, nicht die Reihenfolge.
 */
export function basisAus(pfad: string): (typeof W_BASEN)[number] {
  const p = pfad.replace(/^#/, "");
  return W_BASEN.find((b) => p === b || p.startsWith(`${b}/`)) ?? "/widerrufe";
}

/**
 * Die Kundenbetreuung hat zwei Schreibweisen: in D1 (`berechtigungen.bereich`) heisst sie
 * **`kb`**, als URL-Slug **`kundenbetreuung`** (`bereichToSlug` im Backend).
 * 🔴 04.10.2026: Hier wurde nur gegen `"kundenbetreuung"` verglichen — die Rollen kommen aber
 * ROH aus D1 an (`src/middleware/auth.ts`), also als `"kb"`. Eine KB-Bereichsleitung ohne
 * Admin-Recht war damit nie Bereichsleitung. 📏 Unbemerkt, weil jede BL bisher auch Admin ist
 * (Kommentar `src/lib/register-rechte.ts`, 28.09.). ⇒ Beide Seiten in DIESELBE Form.
 */
function bereichsForm(b: string): string {
  const s = String(b ?? "").trim().toLowerCase();
  return s === "kundenbetreuung" ? "kb" : s;
}

/** Bereichsleitung eines Bereichs? Muster wie in `hub.ts`; `kb` und `kundenbetreuung` sind derselbe Bereich. */
export function istBl(user: UserContext, bereich: string): boolean {
  const ziel = bereichsForm(bereich);
  return (user.berechtigungen ?? []).some(
    (b) => b.rolle.toLowerCase() === "bereichsleiter" && bereichsForm(b.bereich) === ziel
  );
}

/** Die Rolle, aus der heraus gearbeitet wird — Adresse plus Berechtigung. */
export function rolleAus(basis: string, user: UserContext): WRolle {
  if (basis === "/finance/widerrufe") return "finance";
  if (basis === "/kb/widerrufe") {
    return user.isAdmin || istBl(user, "kundenbetreuung") ? "kb" : "kb_ma";
  }
  return "vertrieb";
}

/**
 * Spiegel von `darfQuittieren` in `src/routes/widerruf.ts:65`.
 *
 * 🔑 Der Kommentar dort trägt die andere Hälfte: *„ein ausgeblendeter Knopf ist
 * keine Berechtigung."* Diese Funktion entscheidet ausschließlich, ob ein Knopf
 * **gezeichnet** wird — die Prüfung bleibt beim Server, und wenn beide
 * auseinanderlaufen, gewinnt der Server mit einer Meldung im Klartext.
 */
export function darfQuittieren(user: UserContext, partei: QuittungPartei): boolean {
  if (user.isAdmin) return true;
  const b = (user.allowedBereiche ?? []).map((x) => String(x).toLowerCase());
  if (b.includes("*")) return true;
  if (partei === "vertrieb") return b.includes("vertrieb") || istBl(user, "vertrieb");
  if (partei === "kb") return istBl(user, "kundenbetreuung");
  return istBl(user, "finance");
}

/** Zurücknehmen und Stornieren: BL eines der drei Bereiche oder Admin (`BL`-Policy). */
export function darfRueckweg(user: UserContext): boolean {
  return !!user.isAdmin
    || istBl(user, "vertrieb") || istBl(user, "kundenbetreuung") || istBl(user, "finance");
}

/** Die Partei, für die man an dieser Adresse quittiert. `kb_ma` quittiert nicht. */
export function parteiVon(rolle: WRolle): QuittungPartei | null {
  if (rolle === "vertrieb") return "vertrieb";
  if (rolle === "kb") return "kb";
  if (rolle === "finance") return "finance";
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// ZUSTAND · FRIST · QUITTUNGEN
// ═══════════════════════════════════════════════════════════════════════════

const Z_KLASSE: Record<WiderrufZustand, string> = {
  eingegangen: "z-eingegangen",
  in_bearbeitung: "z-bearbeitung",
  frist_abgelaufen: "z-abgelaufen",
  ergebnis_gemeldet: "z-ergebnis",
  // 🔑 **Eigene Klasse, nicht `z-ergebnis` mitbenutzt.** Der Zustand sieht dem
  // „Ergebnis gemeldet" ähnlich (es ist etwas passiert, es fehlt noch etwas),
  // aber er ist der einzige, in dem alle drei Bereiche fertig sind und trotzdem
  // nichts weitergeht. *Zwei Lagen mit einer Farbe zu zeigen heisst, die
  // seltenere unsichtbar zu machen.*
  wartet_auf_bestaetigung: "z-wartet",
  abgeschlossen: "z-abgeschlossen",
  storniert: "z-storniert"
};

const Z_TEXT: Record<WiderrufZustand, string> = {
  eingegangen: "Eingegangen",
  in_bearbeitung: "In Bearbeitung",
  frist_abgelaufen: "Frist abgelaufen",
  ergebnis_gemeldet: "Ergebnis gemeldet",
  wartet_auf_bestaetigung: "Wartet auf Bestätigung",
  abgeschlossen: "Abgeschlossen",
  storniert: "Storniert"
};

export function zustandPille(z: WiderrufZustand): string {
  return `<span class="z ${Z_KLASSE[z]}"><span class="dot"></span>${escapeHtml(Z_TEXT[z])}</span>`;
}

/** Kalendertage zwischen zwei ISO-Daten. Negativ, wenn `bis` schon vorbei ist. */
export function tageBis(heute: string, bis: string): number {
  const a = Date.parse(`${heute}T12:00:00Z`);
  const b = Date.parse(`${bis}T12:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0;
  return Math.round((b - a) / 86400000);
}

export type FristLageAnzeige = {
  /** `""` (gewahrt/neutral) · `is-run` · `is-over` · `is-none` */
  kl: "" | "is-run" | "is-over" | "is-none";
  wert: string;
  zusatz: string;
};

/**
 * Die Frist eines Falls in der Bauform, die der Entwurf verlangt: **drei
 * Gestalten, nicht eine mit drei Farben.**
 *
 * 🔑 Der Entwurf zählt in **Werktagen** („2 Werktage") — und seit dem
 * 30.08. abends kann die Ansicht das auch: `fallAnsicht` liefert
 * `frist_werktage` mit, gerechnet im Worker, wo die Feiertagsliste liegt.
 *
 * ⚠️ Fällt das Feld aus (alter Client gegen neuen Server oder umgekehrt), wird
 * auf **Kalendertage** zurückgefallen — und dann steht auch „Tage" da, nicht
 * „Werktage". 🔑 *Eine Zahl mit dem falschen Etikett ist schlimmer als eine
 * gröbere mit dem richtigen.*
 *
 * 🔑 `frist_bis` leer heißt NICHT „sofort fällig", sondern **es läuft keine
 * Uhr**: der Fall kam außerhalb der Widerrufsfrist herein oder der Verein hat
 * keine eingetragene Vereinseinrichtung (`zustandVon` fragt die Frist deshalb
 * gar nicht erst ab). Ein leerer Countdown sähe aus wie ein Fehler.
 */
export function fristLage(f: WiderrufFall, heute: string): FristLageAnzeige {
  if (f.ergebnis) {
    return {
      kl: "", wert: "Gewahrt",
      zusatz: f.frist_bis
        ? `Ergebnis am ${appFormatDate(f.ergebnis_am.slice(0, 10))}, Frist lief bis ${appFormatDate(f.frist_bis)}`
        : "Ergebnis liegt vor — es lief ohnehin keine Uhr"
    };
  }
  if (!f.frist_bis) {
    /**
     * ═══ 📏 DER WORTLAUT — behoben 31.08. ══════════════════════════════════
     * Die Vorlage (`widerrufe-uebersicht__alle.html`) schreibt hier:
     * *„läuft keine Uhr — der Fall kann nicht ablaufen"*.
     *
     * 🔴 Mein Bau sagte stattdessen *„keine Vertriebsfrist gesetzt — …"* und
     * verlor damit den **Satzanfang**, der die Sache trägt. Ich hatte das im
     * Abgleich schon als W-08 notiert (*„die Aussage steht, der Satzanfang
     * nicht"*) — und dann nicht behoben.
     *
     * 🔑 *Ein Befund, der aufgeschrieben und nicht erledigt wird, ist eine
     * Liste, kein Ergebnis.*
     *
     * ⚠️ Die zwei Fälle bleiben unterscheidbar: WARUM keine Uhr läuft, steht
     * dahinter statt davor. Der Grund verdrängt den Satz nicht mehr — der
     * Entwurf trägt beides, und der Satz ist der wichtigere Teil.
     */
    const warum = f.recht_offen
      ? ""
      : ` (das Widerrufsrecht endete am ${appFormatDate(f.frist_ende)}, `
        + `der Widerruf kam am ${appFormatDate(f.eingang_am)})`;
    return {
      kl: "is-none", wert: "Keine Frist",
      zusatz: `läuft keine Uhr — der Fall kann nicht ablaufen${warum}`
    };
  }
  // Werktage vom Server, sonst Kalendertage — und die Einheit wandert mit.
  const werk = typeof f.frist_werktage === "number";
  const rest = werk ? (f.frist_werktage as number) : tageBis(heute, f.frist_bis);
  const einheit = (n: number) => werk
    ? `${n} ${n === 1 ? "Werktag" : "Werktage"}`
    : `${n} ${n === 1 ? "Tag" : "Tage"}`;

  if (rest < 0) {
    return {
      kl: "is-over", wert: "Abgelaufen",
      zusatz: `war ${appFormatDate(f.frist_bis)} · seit ${einheit(-rest)} ohne Ergebnis`
    };
  }
  return {
    kl: "is-run",
    wert: rest === 0 ? "heute fällig" : einheit(rest),
    zusatz: `bis ${appFormatDate(f.frist_bis)} · bei Eingang eingefroren`
  };
}

/** Die Frist als Kasten (Kartenspalte und Detail-Seitenspalte). */
export function fristKarte(f: WiderrufFall, heute: string, kopfzeile = "Frist"): string {
  const l = fristLage(f, heute);
  return `<div class="w-frist ${l.kl}">
    <div class="k">${escapeHtml(kopfzeile)}</div>
    <div class="v">${escapeHtml(l.wert)}</div>
    <div class="s">${escapeHtml(l.zusatz)}</div>
  </div>`;
}

/** Die Frist als Pille — für das „Auf dich wartet"-Band. */
export function fristPille(f: WiderrufFall, heute: string): string {
  const l = fristLage(f, heute);
  const kl = l.kl === "is-none" ? "is-none" : l.kl === "is-over" ? "is-over" : l.kl === "is-run" ? "is-run" : "";
  const text = l.kl === "is-none" ? "keine" : l.wert;
  return `<span class="w-fpill ${kl}"><span class="k">Frist</span>${escapeHtml(text)}</span>`;
}

/**
 * Der Haken in der Quittungs-Spur.
 * 🔄 26.09.2026 exportiert, weil die Kündigungs-Spur ihn braucht.
 * 🔑 *Ein zweiter Haken in einer zweiten Schreibweise („✓") sähe an einem der
 * beiden Register anders aus — und niemand würde je vergleichen.*
 */
export const HAKEN = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
  stroke-width="3" stroke-linecap="round"><path d="M4 12l5 5L20 6"/></svg>`;

// ═══════════════════════════════════════════════════════════════════════════
// DAS INFO-SYMBOL — der Baustein des Glattzugs (Posten 1)
// ═══════════════════════════════════════════════════════════════════════════

/** Lucide `info`, 14 px, stroke 2 — die Vorgabe nennt das Symbol namentlich. */
const INFO_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>`;

/**
 * Der Hinweis hinter einem Symbol im Abschnitts- oder Blockkopf.
 * Vorgabe: `docs/design/register-glattzug/README.md` → „Neue Bausteine".
 *
 * Er ersetzt `regelzeile()` und `.w-sec-note` in **beiden** Registern, und
 * darum steht er hier und nicht je Register ein zweites Mal.
 * 🔑 *Zwei Fassungen desselben Bausteins sehen an einem der beiden Register
 * anders aus — und niemand würde je vergleichen.*
 *
 * @param frage  Der `aria-label`-Text, und zwar **als Frage** („Wie wird
 *               sortiert?"). Reiner Text, wird maskiert.
 * @param inhalt Der Wortlaut, HTML erlaubt (`<b>`, `<span class="va-p">`,
 *               `<span class="w-legend">`). ⚠️ **Nichts weglassen** — die
 *               Vorgabe verlangt den bisherigen Hinweistext vollständig.
 * @param rechts `true` für Symbole rechts im Kopf: der Tooltip läuft sonst aus
 *               dem Bild. Entspricht `.is-r` der Vorgabe.
 */
export function infoSymbol(frage: string, inhalt: string, rechts = false): string {
  return `<span class="va-info${rechts ? " is-r" : ""}">`
    + `<button type="button" aria-label="${escapeHtml(frage)}">${INFO_SVG}</button>`
    + `<span class="va-tip" role="tooltip">${inhalt}</span></span>`;
}

/**
 * Die rechte Seite des Abschnittskopfes: dort stand die Zahl, jetzt steht dort
 * die Sortierregel (Vorgabe §1, „Zahl im Kopf").
 *
 * ⚠️ Sie trägt `margin-left:auto` — sie gehört in **denselben** Flex-Kopf wie
 * `<h2>`, nicht darunter.
 */
export function sortierBand(inhalt: string): string {
  return `<span class="va-sort">${inhalt}</span>`;
}

/** Ein eigener Absatz im Tooltip. Mehrere Hinweise, ein Symbol. */
export function tipAbsatz(inhalt: string): string {
  return `<span class="va-p">${inhalt}</span>`;
}

export const QUITTUNGEN: ReadonlyArray<{ p: QuittungPartei; kurz: string; kl: string }> = [
  { p: "vertrieb", kurz: "Vertrieb", kl: "q-v" },
  { p: "kb", kurz: "KB", kl: "q-kb" },
  { p: "finance", kurz: "Finance", kl: "q-fin" }
];

export function quittungAm(f: WiderrufFall, p: QuittungPartei): string {
  return p === "vertrieb" ? f.q_vertrieb_am : p === "kb" ? f.q_kb_am : f.q_finance_am;
}

export function quittungVon(f: WiderrufFall, p: QuittungPartei): string {
  return p === "vertrieb" ? f.q_vertrieb_von : p === "kb" ? f.q_kb_von : f.q_finance_von;
}

/**
 * Die Quittungs-Spur: **immer alle drei Parteien**, auch die offenen.
 *
 * 🔑 Nur zu zeigen, wer schon gehakt hat, beantwortet die Frage nicht, auf die
 * es ankommt — *auf wen wartet der Fall gerade?*
 *
 * 🔒 Ohne Ergebnis ist die Spur `is-locked`: `widerruf-faelle.ts:678` weist
 * jede Quittung ab, solange kein Ergebnis feststeht.
 */
export function spur(f: WiderrufFall): string {
  const locked = !f.ergebnis;
  const zellen = QUITTUNGEN.map(({ p, kurz, kl }) => {
    const am = quittungAm(f, p);
    return `<span class="w-q ${kl} ${am ? "is-done" : "is-open"}">
      <span class="p">${escapeHtml(kurz)}</span>
      <span class="st">${am ? `${HAKEN}${escapeHtml(appFormatDate(am.slice(0, 10)))}` : "offen"}</span>
    </span>`;
  }).join("");
  const spurHtml = `<div class="w-spur${locked ? " is-locked" : ""}">${zellen}</div>`;
  if (!locked) return spurHtml;
  const schonEine = QUITTUNGEN.some(({ p }) => quittungAm(f, p));
  return `<div>${spurHtml}<span class="w-spurnote">${
    schonEine ? "Weitere Quittungen erst mit Ergebnis." : "Quittieren geht erst, wenn ein Ergebnis vorliegt."
  }</span></div>`;
}

// ═══════════════════════════════════════════════════════════════════════════
// „AUF DICH WARTET" — die Zuordnung Fall → Rolle
// ═══════════════════════════════════════════════════════════════════════════

export type Wartend = { grund: string; cta: string };

/**
 * ═══ DIE GEMEINSAME RIEGEL-FORM (W2, 26.09.2026) ═══════════════════════════
 *
 * > **Justin, 25./26.09.2026:** *„weil Widerrufe im Grunde ja besondere
 * > Kündigungen sind"* · *„es soll 1 zu 1 so aussehen wie das
 * > Widerrufsregister, nur halt für die Kündigungslogiken."*
 *
 * 🔴 **Geteilt wird die REIHENFOLGE, nicht der Wortlaut.** Beide Register
 * entscheiden in genau vier Schritten, wen ein Vorgang gerade angeht:
 *
 *   ① storniert oder abgeschlossen  ⇒ niemanden
 *   ② die Vorbedingung des Vertriebs-Riegels fehlt
 *      *(Widerruf: ein Ergebnis · Kündigung: ein Rückgewinnungs-Stand)*
 *   ③ alle Quittungen stehen, der **vierte Riegel** fehlt
 *      — ein eigener Aufruf, sonst fiele der Fall aus jeder Arbeitsliste
 *   ④ die eigene Quittung fehlt
 *
 * 🔑 ***Genau diese Reihenfolge hatte das Kündigungsregister nicht*** — es
 * zeigte eine statische Marke, abgeleitet aus dem PFAD (`/kb/…` ⇒ KB) statt aus
 * der Rolle des Menschen. 📏 Am Bildschirm sichtbar am 26.09.: im Widerruf steht
 * „NICHT DU — Der Fall wartet auf Kundenbetreuung und Finance", in der Kündigung
 * nichts dergleichen.
 *
 * ⚠️ **Die Sätze kommen vom Aufrufer.** Ein „Ergebnis" ist beim Widerruf eine
 * Feststellung des Vertriebs, bei der Kündigung ein Rückgewinnungs-Versuch;
 * eine Funktion mit zwei Vokabularen wäre eine Funktion mit einem
 * Register-Schalter — und der steht irgendwann falsch.
 * 🔑 *Geteilt wird, was in beiden dasselbe BEDEUTET, nicht was gleich heisst.*
 */
export type RiegelStand = {
  storniert: boolean;
  abgeschlossen: boolean;
  /** Fehlt die Vorbedingung des Vertriebs-Riegels? */
  vorbedingungOffen: boolean;
  /** Offene Quittungen als Parteien — leer heisst: alle drei stehen. */
  offeneQuittungen: readonly QuittungPartei[];
  /** Der vierte Riegel (Bestätigung an den Verein). */
  vierterOffen: boolean;
  /** Hat diese Partei schon quittiert? */
  quittiert: (p: QuittungPartei) => boolean;
  /** Die Sätze — je Zweig einer, vom Register gestellt. */
  worte: {
    /** Pro Rolle ein Satz für Schritt ②. Fehlt die Rolle, wartet sie dort nicht. */
    vorbedingung: Partial<Record<WRolle, Wartend>>;
    /** Schritt ③ — der vierte Riegel. */
    vierterRiegel: Wartend;
    /** Schritt ④ — die eigene Quittung fehlt. */
    quittungFehlt: Wartend;
  };
};

/**
 * Wartet dieser Vorgang auf mich? `null` = nein. **Die Reihenfolge, beiden
 * Registern gemeinsam.**
 *
 * ⚠️ Schritt ③ steht **vor** der Quittungs-Prüfung, nicht danach. Stehen alle
 * Quittungen, gäbe Schritt ④ für jede Rolle `null` — der Fall wäre „fertig für
 * alle" und schlösse trotzdem nicht.
 * 🔑 ***Das ist die Lage, die man eine Überraschung nennt: niemand ist
 * zuständig, und es ist trotzdem nicht vorbei.***
 *
 * ⚠️ **Finance wird beim vierten Riegel bewusst nicht gerufen:** die Bestätigung
 * geht an den Verein, und den Kontakt dorthin haben Vertrieb und
 * Kundenbetreuung. `kb_ma` dagegen schon — dieser Riegel hängt an keiner Rolle,
 * sondern an dem, der die Mail geschickt hat.
 */
export function wartetAufStand(s: RiegelStand, rolle: WRolle): Wartend | null {
  if (s.storniert || s.abgeschlossen) return null;

  if (s.vorbedingungOffen) return s.worte.vorbedingung[rolle] ?? null;

  if (s.vierterOffen && s.offeneQuittungen.length === 0 && rolle !== "finance") {
    return s.worte.vierterRiegel;
  }

  if (rolle === "kb_ma") return null;
  const p = parteiVon(rolle);
  if (!p || s.quittiert(p)) return null;
  return s.worte.quittungFehlt;
}

/**
 * Wartet dieser Fall auf mich? `null` = nein.
 *
 * 🔴 **Ein KB-Mitarbeiter ohne Bereichsleitung wird von der Quittung NICHT
 * angesprochen** — für die Kundenbetreuung quittiert nur Maxi. Ohne diese
 * Unterscheidung zählte das Band Arbeit mit, die er nicht erledigen kann, und
 * genau daran ist in der Terminstrecke schon einmal eine Arbeitsliste
 * unglaubwürdig geworden.
 */
export function wartetAuf(f: WiderrufFall, rolle: WRolle, heute: string): Wartend | null {
  // 🔄 **26.09.2026 — dieselbe Funktion, jetzt auf der geteilten Reihenfolge.**
  // Die vier Schritte stehen in `wartetAufStand`; hier bleiben die Sätze, die
  // nur der Widerruf sagen kann. Die 18 Fälle in `widerruf-rollen.test.ts` sind
  // die Gegenprobe: ändert sich das Verhalten, werden sie rot.
  // 🔑 *Ein Umbau ohne Gegenprobe ist eine Behauptung über sich selbst.*
  const abgelaufen = !!f.frist_bis && f.frist_bis < heute;
  const lang = f.bestaetigung_werktage !== null && f.bestaetigung_werktage > 2;

  // ② Ohne Ergebnis: Vertrieb und Kundenbetreuung — Finance wartet erst darauf.
  const ohneErgebnis: Partial<Record<WRolle, Wartend>> = {
    vertrieb: abgelaufen
      ? { grund: `Die Frist ist am <b>${escapeHtml(appFormatDate(f.frist_bis))}</b> abgelaufen, ohne dass ein Ergebnis dasteht.`, cta: "Ergebnis melden" }
      : f.frist_bis
        ? { grund: "Rückgewinnungs-Versuch offen — danach läuft die Frist ab.", cta: "Ergebnis melden" }
        : { grund: "Es läuft <b>keine Frist</b>: niemand wird an diesen Fall erinnert.", cta: "Ergebnis melden" }
  };
  const kbSatz: Wartend = abgelaufen
    ? { grund: "<b>Kein Ergebnis</b> nach abgelaufener Frist — der Fall braucht eine Feststellung.", cta: "Ergebnis melden" }
    : f.frist_bis
      ? { grund: "Noch kein Ergebnis — der Vertrieb hat die Frist, die Feststellung liegt bei dir.", cta: "Ergebnis melden" }
      : { grund: "Eingegangen ohne Frist — <b>niemand wird erinnert, außer dir</b>.", cta: "Bearbeiten" };
  ohneErgebnis.kb = kbSatz;
  ohneErgebnis.kb_ma = kbSatz;

  return wartetAufStand({
    storniert: !!f.storniert_am,
    abgeschlossen: !!f.abgeschlossen_am,
    vorbedingungOffen: !f.ergebnis,
    offeneQuittungen: f.offene_quittungen,
    vierterOffen: !!f.offene_bestaetigung,
    quittiert: (p) => !!quittungAm(f, p),
    worte: {
      vorbedingung: ohneErgebnis,
      vierterRiegel: {
        // 🔄 **29.09.2026:** die Bestätigung ist der ERSTE Schritt (Entscheid
        // Justin), also nennt der Satz den Eingang und nicht die Quittungen.
        grund: lang
          ? `Der Widerruf liegt seit <b>${escapeHtml(appFormatDate(f.bestaetigung_uhr_ab))}</b> vor — `
            + `die <b>Bestätigung an den Verein</b> fehlt seit ${escapeHtml(String(f.bestaetigung_werktage))} Werktagen.`
          : `Der Verein soll als Erstes wissen, dass sein Widerruf angekommen ist: die `
            + `<b>Bestätigung an den Verein</b> fehlt noch.`,
        cta: "Bestätigen"
      },
      quittungFehlt: {
        grund: `Ergebnis <b>${escapeHtml(f.ergebnis_label)}</b> steht seit `
          + `${escapeHtml(appFormatDate(f.ergebnis_am.slice(0, 10)))} — <b>deine Quittung fehlt.</b>`,
        cta: "Quittieren"
      }
    }
  }, rolle);
}

/**
 * Die `data-du`-Marken einer Fallkarte. Es wird nur die EIGENE Rolle gesetzt:
 * der Klickdummy trägt alle vier, weil er umschalten kann — hier gibt es nur
 * eine, und eine Marke für eine Rolle, die man nicht hat, wäre Anzeige ohne
 * Adressaten.
 */
export function duMarke(f: WiderrufFall, rolle: WRolle, heute: string): string {
  if (!wartetAuf(f, rolle, heute)) return "";
  return rolle === "vertrieb" ? "v" : rolle === "kb" ? "kb" : rolle === "kb_ma" ? "kbma" : "fin";
}

/**
 * ⛔ **`duFahne` ist am 04.10.2026 entfallen — E7.**
 *
 * Sie zeichnete die Fahne „Du bist dran" an die **Listenkarte**. Die Vorgabe
 * streicht sie dort ganz: die Karte sagt dasselbe schon über `data-du` (sie
 * färbt sich lime), über die Quittungs-Spur und über das Band darüber.
 * 🔑 ***Eine Aussage, die eine Liste viermal macht, wird beim zweiten Mal
 * überlesen und beim dritten misstraut.***
 *
 * ⚠️ **E9 lässt die Fahne im Aufruf-Band des FALLS stehen** — dort hat sie kein
 * Lime, das sie ersetzt. Das Band baut sie selbst (`w-call` + `span.tag`), es
 * hat diese Funktion nie benutzt.
 * 📌 `duMarke` bleibt: sie liefert `data-du`, und daran hängt die Einfärbung.
 *
 * 📏 Gefunden hat die Leiche `tote-exporte.test.ts` — nicht ich.
 */

// ═══════════════════════════════════════════════════════════════════════════
// HÜLLE
// ═══════════════════════════════════════════════════════════════════════════

/** Die Hülle jedes Schirms. `data-role` sitzt HIER, nicht auf `<body>`. */
export function huelle(rolle: WRolle, inhalt: string): string {
  return `<div class="ve2-wrap" data-role="${escapeHtml(rolle)}"><div class="ve2-container">${inhalt}</div></div>`;
}

/**
 * DIE REITER (Justin, 11.09.2026: *„hätte gerne einfach mehrere Tabs, die ich
 * bei Klick auf Widerrufe auswählen kann"*).
 *
 * ═══ 🔑 WARUM REITER UND NICHT WEITER SCROLLEN ═════════════════════════════
 * Die Übersicht trug fünf Abschnitte untereinander. Die ersten drei beantworten
 * **eine** Frage („woran arbeite ich"), die letzten zwei zwei ganz andere
 * („läuft etwas auseinander", „wer hat welche Quote"). Untereinander gelegt
 * musste man an der Arbeit vorbeiscrollen, um zur Auswertung zu kommen — und
 * die Auswertung sah aus wie ein Nachtrag zur Arbeitsliste.
 * 🔑 *Was man nacheinander liest, hält man für aufeinanderfolgend.*
 *
 * ⚠️ **Es sind Sichten auf DIESELBEN Daten, keine eigenen Seiten.** Der
 * Übersichts-Endpunkt liefert weiterhin alles in einem Aufruf; der Reiter
 * entscheidet nur, was davon gezeigt wird. *Fünf Reiter mit fünf Ladezuständen
 * wären fünf Gelegenheiten, einen davon zu vergessen.*
 *
 * 📌 Die Routen hängen an der BASIS (`/widerrufe` · `/kb/widerrufe` ·
 * `/finance/widerrufe`) — wer aus Finance kommt, bleibt in Finance.
 */
/**
 * ═══ 🔄 18.09.2026 — AUS EINER ZEILE WERDEN ZWEI GRUPPEN ═══════════════════
 *
 * > **Justin, 17.09.:** *„Ich möchte nochmal an der Übersichtlichkeit arbeiten,
 * > finde es noch recht voll und nicht wirklich einfach zu bedienen/verstehen
 * > wo was ist und wirkt noch sehr unaufgeräumt."*
 * > **Justin, 17.09.:** *„Vorgänge und Prüfung finde ich glaube nicht schlecht."*
 *
 * 📏 Gemessen vor dem Umbau: **sechs** Reiter in EINER Zeile — Offene Fälle ·
 * Abgeschlossen · Offene Widerrufsrechte · Frist nicht bestimmbar ·
 * Register ⇄ HubSpot · Quote je Closer.
 *
 * Sie beantworten **drei** verschiedene Fragen, und das sah man ihnen nicht an:
 * woran arbeite ich · läuft etwas auseinander · wie steht es um die Zahlen.
 * 🔑 ***Eine Reiterzeile ist eine Behauptung, die Einträge seien gleichartig.***
 * Sechs nebeneinander behaupten das sechsmal — und zwingen den Leser, die
 * Gliederung selbst zu erfinden.
 *
 * ⇒ Die Gliederung wandert in die **Sidebar-Gruppe** (`bereiche.ts`), die
 * Reiterzeile trägt nur noch die Geschwister EINER Frage.
 *
 * ⚠️ „Quote je Closer" ist **kein** Reiter mehr — die Zahlen gehören ins
 * Dashboard-Analysefeld (Entwurf v3 ④). Die Route bleibt bestehen, damit alte
 * Lesezeichen nicht ins Leere laufen; sie ist nur nicht mehr verlinkt.
 */
export const W_REITER_VORGAENGE: ReadonlyArray<{ pfad: string; label: string }> = [
  /**
   * 🔴 **Der Schnitt, an dem der ganze Entwurf hängt** (Justin, 17.09.:
   * *„Vorgänge würde ich in 4 Abschnitte teilen. Offene Fälle die bei mir
   * liegen … weiterhin offene Fälle weil bspw. KB und Finance noch nicht
   * quittiert haben … und dann Melden."*).
   *
   * Die Regel steht schon im Baum und hiess bisher nur nicht so: `wartetAuf()`.
   * Sie war ein **Band über** der Liste (`mineBand`), die Liste selbst zeigte
   * beides gemischt. 🔑 *Eine Hervorhebung innerhalb einer Liste sagt „das hier
   * auch" — ein eigener Reiter sagt „nur das hier".*
   *
   * ⚠️ **„Bei mir" ist, was MEINE ROLLE quittieren oder feststellen darf** —
   * nicht, was mich interessiert. Beim Rollenwechsel wandern Fälle deshalb
   * sichtbar zwischen den ersten beiden Reitern. Das ist kein Nebeneffekt,
   * sondern der Beleg dafür, dass „ein Vorgang, drei Bereiche" stimmt.
   */
  { pfad: "", label: "Bei mir" },
  { pfad: "/offen", label: "Weiterhin offen" },
  /**
   * ═══ 🔴 DER ABSCHLUSS BRAUCHT EINEN EIGENEN REITER — Justin, 14.09.2026 ═══
   * *„die abgeschlossenen widerrufe müssen bitte als Tab aufrufbar bleiben"*
   *
   * 📏 Nachgemessen am Code, nicht vermutet: Bis hierher waren geschlossene
   * Fälle **nur im Leerzustand** von ① zu sehen (`voidFertig`, und dort nur die
   * letzten 12). Sobald EIN offener Fall existierte, blieb von ihnen die
   * Zeichenfolge *„· N geschlossen"* in der Kopfzeile — anklickbar war nichts.
   * 📏 Am 14.09. stehen 11 Fälle im Register, 5 davon geschlossen ⇒ genau die
   * Lage, in der sie unerreichbar sind.
   *
   * 🔑 ***Ein Archiv, das nur sichtbar ist, solange nichts zu tun ist, ist im
   * Regelfall nicht sichtbar.*** Und der Regelfall ist der, für den man baut.
   *
   * ⚠️ **Kein Zähler an diesem Reiter**, obwohl die Zahl vorliegt: sie
   * beschreibt keine Menge Arbeit (dieselbe Regel wie an „Quote je Closer").
   * Sie steht in der Kopfzeile des Abschnitts, wo sie ein Beleg ist.
   */
  { pfad: "/abgeschlossen", label: "Abgeschlossen" },
  { pfad: "/melden", label: "Melden" }
  /**
   * 🔄 **05.10.2026 — „Liste" ist hier raus und steht in der Sidebar**
   * (`bereiche.ts`, `widerrufsGruppe`). > Justin: *„Liste bei Widerrufe und
   * Kündigungen gerne als eigener Punkt in die Sidebar und raus aus den Tabs."*
   * 🔑 Sie war nie ein Reiter dieser Ansicht: eigener Schirm, eigener Zeitraum
   * — wie die Closingliste, die auch ein eigener Sidebar-Punkt ist.
   */
];

/**
 * Die zweite Gruppe: **Prüfung** — nicht Arbeit an einem Vorgang, sondern die
 * Frage, ob der Bestand mit sich selbst übereinstimmt.
 *
 * 🔑 *Der Unterschied ist nicht die Datenmenge, sondern der Adressat: „Vorgänge"
 * fragt „was tue ich", „Prüfung" fragt „stimmt es".* Wer das in eine Zeile legt,
 * lässt beide Fragen gleich dringend aussehen.
 */
export const W_REITER_PRUEFUNG: ReadonlyArray<{ pfad: string; label: string }> = [
  { pfad: "/abgleich", label: "Register ⇄ HubSpot" },
  /**
   * 🔄 **22.09.2026 — „Absprachen prüfen" wandert hierher.**
   *
   * Sie stand als eingeklappter `<details>`-Block **unten im Reiter
   * „Vorgänge"** — unter vierzehn Fallkarten, in einer Liste, die eine ganz
   * andere Frage beantwortet.
   *
   * 🔑 ***Die Zeilen sind keine Widerrufe.*** Es ist eine Zeile je VEREIN, aus
   * `verein_vorbehalt`: eine Mail oder Notiz sagt etwas anderes über die
   * Widerrufsfrist als der Vertrag. Sie sitzt damit **vor** dem Register, nicht
   * darin — sie entscheidet über die Frist, aus der sich später ergibt, ob ein
   * Widerruf rechtzeitig war. Ein Fall muss dafür gar nicht existieren.
   *
   * ⇒ Sie gehört zu „Prüfung", und zwar wörtlich zu dessen eigener Frage:
   * *„läuft etwas auseinander?"* Neben „Register ⇄ HubSpot" steht sie richtig;
   * unter den Vorgängen stand sie nur da, wo Platz war.
   * 🔑 *Ein Abschnitt am Ende einer fremden Liste ist keine Einordnung, sondern
   * ein Anhang — und Anhänge liest man zuletzt oder gar nicht.*
   */
  { pfad: "/absprachen", label: "Absprachen prüfen" },
  { pfad: "/rechte", label: "Offene Widerrufsrechte" },
  /**
   * 🔄 **17.09.2026: hiess „Ohne Vereinseinrichtung", umbenannt zu „Frist nicht
   * bestimmbar".**
   *
   * 📏 Am 17.09. standen in zwei Bereichen gleichzeitig zwei Reiter mit
   * DEMSELBEN Wort und verschiedenen Zahlen:
   *     Widerrufe › „Ohne Vereinseinrichtung" ......  5
   *     Onboarding-Termine › „Ohne Vereinseinrichtung" .. 24
   * Beide Zahlen sind richtig. Sie beantworten nur verschiedene Fragen:
   *   · 24 = wo liegt **Onboarding-Arbeit** (ganzer aktiver Bestand nach dem
   *     Altbestands-Stichtag) — `ve-liste-offen.ts`,
   *   · 5 = wessen **erste Rechnung** bis zum nächsten Lauf (1./15.) ohne
   *     bestimmbare Widerrufsfrist rausgeht — `ve-rechnungsrisiko.ts`.
   *
   * 🔑 ***Ein Name für zwei Zahlen ist die Fehlerklasse, die dieses Projekt
   * schon dreimal Geld gekostet hat*** (so steht es in `metric-defs.ts`). Der
   * Reiter daneben macht es richtig: „Offene Widerrufsrechte" nennt seine
   * FRAGE, nicht seine Datenlage. Dieser nannte die Datenlage — und die teilt
   * er mit einem Schirm in einem anderen Bereich.
   *
   * ⚠️ Die Abschnitts-Überschrift bleibt „Ohne eingetragene Vereinseinrichtung":
   * dort steht die Frage vollständig daneben, und wer angekommen ist, braucht
   * die Datenlage. Verwechselbar ist nur die Beschriftung im Menü.
   */
  { pfad: "/luecke", label: "Frist nicht bestimmbar" }
];

/**
 * Alle Reiter beider Gruppen — für `reiterAus`, damit ein Pfad aus **jeder**
 * Gruppe erkannt wird.
 *
 * ⚠️ `/quote` steht bewusst mit drin, obwohl es in keiner Zeile mehr erscheint:
 * sonst stünde die Ansicht auf „nichts aktiv" und sähe aus wie ein Fehler.
 * 🔑 *Eine Adresse, die es noch gibt, muss sich auch noch wiedererkennen.*
 */
export const W_REITER_ALLE: ReadonlyArray<{ pfad: string; label: string }> = [
  ...W_REITER_VORGAENGE, ...W_REITER_PRUEFUNG, { pfad: "/quote", label: "Quote je Closer" }
];

/** Zu welcher Gruppe gehört dieser Reiter-Pfad? */
export function wGruppe(pfad: string): "vorgaenge" | "pruefung" {
  return W_REITER_PRUEFUNG.some((r) => r.pfad === pfad) ? "pruefung" : "vorgaenge";
}

/** Welcher Reiter ist gemeint? `""` = der erste. */
export function reiterAus(pfad: string, basis: string): string {
  const rest = pfad.replace(/^#/, "").slice(basis.length);
  // ⚠️ Nur die bekannten Reiter zählen. `/fall/W-2026-0001` und `/melden`
  // sind KEINE Reiter — sie sind eigene Schirme unter derselben Basis und
  // dürfen die Leiste nicht auf „nichts aktiv" stellen.
  const treffer = W_REITER_ALLE.find((r) => r.pfad && rest === r.pfad);
  if (treffer) return treffer.pfad;
  // 🔴 18.09.2026: `/melden/<companyId>` ist derselbe Reiter wie `/melden`.
  // Die Meldemaske kommt mit Vorauswahl aus den Prüfungs-Listen — ohne diese
  // Zeile stünde die Leiste dort auf „Bei mir", also auf einem Reiter, auf dem
  // man gerade NICHT ist.
  // 🔑 *Eine Leiste, die den falschen Eintrag hervorhebt, ist schlimmer als
  // eine, die keinen hervorhebt: die erste lügt, die zweite schweigt.*
  if (rest.startsWith("/melden/")) return "/melden";
  return "";
}

/**
 * Die Reiterleiste. `basis` ist der Einstieg, `aktiv` der Pfad-Rest.
 *
 * ⚠️ Jeder Reiter trägt seine **eigene Adresse** (kein `<button>`): so
 * funktionieren Zurück-Taste, Lesezeichen und „in neuem Tab öffnen".
 * 🔑 *Ein Reiter ohne Adresse ist ein Zustand, den man niemandem schicken kann.*
 *
 * ═══ 🔴 UND DESHALB OHNE `#` — eine Korrektur vom 11.09.2026 ═══════════════
 * Die erste Fassung schrieb `href="#/widerrufe/rechte"`, nach dem Muster von
 * `ve-rahmen.ts`. 📏 **Dieser Router arbeitet aber über die History-API**
 * (`router.ts:121` — `window.history.pushState`), der Hash wird nirgends
 * gelesen. Der Klick funktionierte trotzdem, weil der Handler ihn abfängt —
 * **„in neuem Tab öffnen" und ein Lesezeichen landeten auf der aktuellen Seite
 * mit totem Hash.**
 *
 * 🔑 ***Ein `href`, das nur der Klick-Handler rettet, ist keine Adresse — es
 * ist Dekoration, die wie eine aussieht.*** Genau daran ist dieselbe Ansicht
 * schon einmal hängengeblieben: `basisAus` las bis zum 31.08. `location.hash`
 * und fiel deshalb still in die Vertriebs-Sicht zurück.
 *
 * ⚠️ `ve-rahmen.ts` trägt denselben `#`-Rest. Dort ist er nicht korrigiert,
 * weil er nicht zu diesem Bau gehört — aber er hat dasselbe Verhalten.
 */
export function wReiter(basis: string, aktiv: string, zaehler: Record<string, number> = {}): string {
  // 🔑 Die Zeile zeigt nur die GESCHWISTER des aktiven Reiters. Wer in
  // „Prüfung" steht, sieht keine Vorgänge-Reiter — dafür ist die Sidebar da.
  // *Eine Reiterzeile, die alles zeigt, ist wieder das, was sie ersetzen soll.*
  const zeile = wGruppe(aktiv) === "pruefung" ? W_REITER_PRUEFUNG : W_REITER_VORGAENGE;
  return `<nav class="ts-tabs w-tabs">
    ${zeile.map((r) => {
      const n = zaehler[r.pfad || "/"];
      return `<a href="${escapeHtml(basis + r.pfad)}" data-w-reiter="${escapeHtml(basis + r.pfad)}"
        class="${r.pfad === aktiv ? "is-on" : ""}">${escapeHtml(r.label)}${
        // Der Zähler steht NUR dort, wo er eine Menge Arbeit beschreibt —
        // nicht an der Auswertung. *Eine Zahl am Reiter liest sich wie eine
        // Aufgabenzahl; an einer Auswertung wäre sie eine Behauptung.*
        // 🆕 25.09. — der Absprachen-Zähler ist VIOLETT wie seine Liste.
        // 🔑 *Eine Farbe, die nur an einer Stelle auftaucht, ist Dekoration;
        // dieselbe Farbe an Reiter, Hinweis und Liste ist ein Weg.*
        typeof n === "number"
          ? `<span class="cnt${r.pfad === "/absprachen" && n > 0 ? " is-vio" : ""}">${n}</span>`
          : ""
      }</a>`;
    }).join("")}
  </nav>`;
}

/** Der Seitenkopf: Titel (mit optionaler Zustandspille), Unterzeile, Aktionen. */
export function kopf(a: {
  titel: string; pille?: string; unter: string; aktionen?: string;
  /**
   * 🆕 25.09.2026 — eine zusätzliche Klasse am Kopf, für Ansichten mit eigener
   * Signatur (heute: das Dashboard v4 mit dem Lime-Strich vor dem Titel).
   *
   * 🔑 ***Der Titel bleibt escaped.*** Erste Fassung wollte das Markup durch
   * `titel` schleusen — es wäre als Text erschienen, weil `escapeHtml` genau
   * dafür da ist. *Wer eine Auszeichnung braucht, hängt sie an den Behälter,
   * nicht in den Text.*
   */
  kl?: string;
}): string {
  return `<div class="w-dhead${a.kl ? ` ${escapeHtml(a.kl)}` : ""}">
    <div>
      <h1>${escapeHtml(a.titel)}${a.pille ? ` ${a.pille}` : ""}</h1>
      <div class="sub">${a.unter}</div>
    </div>
    <div class="w-dacts">${a.aktionen ?? ""}</div>
  </div>`;
}

/** Eine Angabe in der Unterzeile — Label mager, Wert fett. */
export function unterAngabe(k: string, v: string): string {
  return v ? `<span>${escapeHtml(k)} <b>${escapeHtml(v)}</b></span>` : "";
}

export function parteiLabel(p: QuittungPartei): string {
  return PARTEI_LABEL[p];
}

/**
 * DIE ARBEITSREIHENFOLGE — dringlichste zuerst (Entscheid Justin, 20.09.2026).
 *
 * > Vorher: Registerreihenfolge (Fall-ID aufsteigend). Vorhersagbar, und
 * > **stumm** — die Liste sagte nichts über Dringlichkeit.
 *
 * ═══ 🔑 VIER RÄNGE, UND DER ERSTE IST NICHT DER SCHLIMMSTE ═════════════════
 * ① **laufende Frist** — die nächste zuerst. *Hier kann man noch handeln.*
 * ② **abgelaufene Frist** — die am längsten überfällige zuerst.
 * ③ **kein Ergebnis, keine Frist** — der älteste Eingang zuerst.
 * ④ **Ergebnis liegt vor** — es fehlen nur Haken, es läuft keine Uhr.
 *
 * ⚠️ **Rang ② steht unter ①, obwohl er schlimmer ist.** Eine abgelaufene Frist
 * ist ein Verlust, der bereits eingetreten ist; eine laufende ist der letzte
 * Tag, an dem er sich noch vermeiden lässt.
 * 🔑 ***Eine Arbeitsliste sortiert nach Beeinflussbarkeit, nicht nach
 * Schwere*** — sonst steht oben, was man nur noch zur Kenntnis nehmen kann.
 *
 * ⚠️ **Rang ③ nach Eingang, nicht nach Frist** — es gibt keine. Ohne diesen
 * eigenen Rang landete jeder fristlose Fall ganz oben oder ganz unten, je
 * nachdem, wie ein leeres Datum verglichen wird. *Ein leerer Wert ist keine
 * Sortiergrundlage, er ist ein eigener Fall.*
 *
 * ⛔ Die Fall-ID bleibt der **letzte** Schlüssel. Ohne sie wäre die Reihenfolge
 * bei gleicher Frist von der Datenbank abhängig — und damit an zwei Tagen
 * verschieden, ohne dass sich etwas geändert hat.
 */
export function dringlichkeit(f: WiderrufFall, heute: string): [number, string, string] {
  if (f.ergebnis) return [4, f.ergebnis_am || "9999", f.fall_id];
  if (!f.frist_bis) return [3, f.eingang_am || "9999", f.fall_id];
  return f.frist_bis >= heute
    ? [1, f.frist_bis, f.fall_id]
    : [2, f.frist_bis, f.fall_id];
}

/** Die Liste in Arbeitsreihenfolge. Verändert die Eingabe NICHT. */
export function nachDringlichkeit(faelle: WiderrufFall[], heute: string): WiderrufFall[] {
  return [...faelle].sort((a, b) => {
    const [ra, sa, ia] = dringlichkeit(a, heute);
    const [rb, sb, ib] = dringlichkeit(b, heute);
    return ra - rb || sa.localeCompare(sb) || ia.localeCompare(ib);
  });
}

/** Der Satz, der die Reihenfolge erklärt — an EINER Stelle wie die Regel. */
export const DRINGLICHKEIT_REGEL =
  `Sortiert nach <b>Dringlichkeit</b>: laufende Frist (nächste zuerst) · abgelaufene `
  + `Frist · ohne Frist (ältester Eingang) · Ergebnis liegt vor. 🔑 Die laufende Frist `
  + `steht über der abgelaufenen — <b>eine Arbeitsliste sortiert nach `
  + `Beeinflussbarkeit, nicht nach Schwere</b>.`;
